<script lang="ts">
	/**
	 * Context Menu Component
	 *
	 * Custom right-click menu that shows relevant commands based on selection.
	 * Commands are grouped by section and filtered by dynamic visibility.
	 * Supports submenus via the `submenu` field on commands.
	 */
	import { get } from 'svelte/store';
	import { onMount, onDestroy, tick } from 'svelte';
	import { canvasEditor, nodes, selectedNodeIds } from '../../stores';
	import { getContextMenuCommandsBySection } from '../../commands/registry';
	import type { Command, ContextMenuState } from '../../commands/types';
	import type { UINode } from '@rool/renderer';
	import { shortcutToDisplay } from '@rool/input';
	import {
		AlignLeft,
		AlignCenterHorizontal,
		AlignRight,
		AlignTop,
		AlignCenterVertical,
		AlignBottom,
		ArrowsOutLineHorizontal,
		ArrowsOutLineVertical,
	} from 'phosphor-svelte';
	import type { Component } from 'svelte';

	// Map icon name strings to phosphor components
	const iconMap: Record<string, Component<{ size?: number }>> = {
		AlignLeft,
		AlignCenterHorizontal,
		AlignRight,
		AlignTop,
		AlignCenterVertical,
		AlignBottom,
		ArrowsOutLineHorizontal,
		ArrowsOutLineVertical,
	};

	type DisplayCommand = Command & { displayLabel: string; checkedState?: boolean | 'mixed' };

	// A menu entry is either a plain command or a submenu group
	type MenuEntry =
		| { kind: 'command'; cmd: DisplayCommand }
		| { kind: 'submenu'; label: string; commands: DisplayCommand[] };

	// Props — `menuState` avoids collision with Svelte 5's $state rune
	interface Props {
		menuState: ContextMenuState;
		onClose: () => void;
	}
	let { menuState, onClose }: Props = $props();

	// Menu element for positioning (bind:this only, not reactive)
	// svelte-ignore non_reactive_update
	let menuElement: HTMLDivElement;

	// Track which submenu is open
	let openSubmenu: string | null = $state(null);

	// Computed commands by section, with submenus grouped
	// Use $store syntax (not get()) so $derived.by tracks reactive dependencies
	let menuSections = $derived.by(() => {
		const currentNodes = $nodes;
		const selected = $selectedNodeIds;
		const selectedNodes = currentNodes.filter((n) => selected.has(n.id));

		// Resolve checked state for a command
		function withChecked(cmd: DisplayCommand): DisplayCommand {
			if (cmd.checked) {
				return { ...cmd, checkedState: cmd.checked(selectedNodes) };
			}
			return cmd;
		}

		// Get both Canvas and Canvas (Selection) commands
		const sections = new Map<string | undefined, DisplayCommand[]>();

		const canvasCommands = getContextMenuCommandsBySection('Canvas', selectedNodes);
		for (const [section, cmds] of canvasCommands) {
			if (!sections.has(section)) sections.set(section, []);
			sections.get(section)!.push(...cmds.map(withChecked));
		}

		if (selectedNodes.length > 0) {
			const selectionCommands = getContextMenuCommandsBySection(
				'Canvas (Selection)',
				selectedNodes
			);
			for (const [section, cmds] of selectionCommands) {
				if (!sections.has(section)) sections.set(section, []);
				sections.get(section)!.push(...cmds.map(withChecked));
			}
		}

		// Transform each section: group submenu commands together
		const result = new Map<string | undefined, MenuEntry[]>();
		for (const [section, cmds] of sections) {
			const entries: MenuEntry[] = [];
			const submenuMap = new Map<string, DisplayCommand[]>();

			for (const cmd of cmds) {
				if (cmd.submenu) {
					if (!submenuMap.has(cmd.submenu)) submenuMap.set(cmd.submenu, []);
					submenuMap.get(cmd.submenu)!.push(cmd);
				} else {
					entries.push({ kind: 'command', cmd });
				}
			}

			// Insert submenu groups in the order they first appeared
			const insertedSubmenus = new Set<string>();
			for (const cmd of cmds) {
				if (cmd.submenu && !insertedSubmenus.has(cmd.submenu)) {
					insertedSubmenus.add(cmd.submenu);
					entries.push({
						kind: 'submenu',
						label: cmd.submenu,
						commands: submenuMap.get(cmd.submenu)!,
					});
				}
			}

			result.set(section, entries);
		}

		return result;
	});

	// Get shortcut display for a command
	function getShortcutDisplay(commandId: string): string | null {
		const editor = get(canvasEditor);
		if (!editor) return null;

		const input = editor.getInputSystem();
		const cmd = input.getCommand(commandId);
		return cmd?.display ?? null;
	}

	// Handle command execution
	function executeCommand(cmd: DisplayCommand, isCheckbox = false) {
		const editor = get(canvasEditor);
		if (editor) {
			const input = editor.getInputSystem();
			const executed = input.executeCommand(cmd.id);
			if (!executed) {
				cmd.execute();
			}
		} else {
			cmd.execute();
		}
		// Checkbox items stay open so the user can toggle multiple flags
		if (!isCheckbox) {
			onClose();
		}
	}

	// Position adjustment to keep menu in viewport
	async function adjustPosition() {
		await tick();
		if (!menuElement) return;

		const rect = menuElement.getBoundingClientRect();
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;

		if (menuState.position.x + rect.width > viewportWidth) {
			menuElement.style.left = `${viewportWidth - rect.width - 8}px`;
		}

		if (menuState.position.y + rect.height > viewportHeight) {
			menuElement.style.top = `${viewportHeight - rect.height - 8}px`;
		}
	}

	// Handle click outside
	function handleClickOutside(e: MouseEvent) {
		if (menuElement && !menuElement.contains(e.target as Node)) {
			onClose();
		}
	}

	// Handle escape key
	function handleKeydown(e: KeyboardEvent) {
		if (e.key === 'Escape') {
			onClose();
		}
	}

	$effect(() => {
		if (menuState.visible) {
			openSubmenu = null;
			adjustPosition();
			document.addEventListener('mousedown', handleClickOutside);
			document.addEventListener('keydown', handleKeydown);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleKeydown);
		};
	});
</script>

{#if menuState.visible}
	<!-- Prevent mousedown from propagating to canvas (which could clear selection) -->
	<!-- svelte-ignore a11y_no_static_element_interactions -->
	<div
		bind:this={menuElement}
		class="context-menu"
		style="left: {menuState.position.x}px; top: {menuState.position.y}px;"
		role="menu"
		tabindex="-1"
		onmousedown={(e) => e.stopPropagation()}
	>
		{#each [...menuSections] as [section, entries], sectionIndex}
			{#if sectionIndex > 0}
				<div class="context-menu-separator"></div>
			{/if}
			{#if section}
				<div class="context-menu-section">{section}</div>
			{/if}
			{#each entries as entry}
				{#if entry.kind === 'command'}
					<button
						class="context-menu-item"
						onclick={() => executeCommand(entry.cmd)}
						role="menuitem"
					>
						<span class="context-menu-label">{entry.cmd.displayLabel}</span>
						{#if getShortcutDisplay(entry.cmd.id)}
							<span class="context-menu-shortcut"
								>{getShortcutDisplay(entry.cmd.id)}</span
							>
						{/if}
					</button>
				{:else}
					<!-- Submenu trigger -->
					<!-- svelte-ignore a11y_no_static_element_interactions -->
					<div
						class="context-menu-submenu"
						onmouseenter={() => (openSubmenu = entry.label)}
						onmouseleave={() => (openSubmenu = null)}
					>
						<button
							class="context-menu-item"
							class:submenu-open={openSubmenu === entry.label}
							role="menuitem"
							aria-haspopup="true"
							aria-expanded={openSubmenu === entry.label}
						>
							<span class="context-menu-label">{entry.label}</span>
							<span class="context-menu-chevron">&#9656;</span>
						</button>
						{#if openSubmenu === entry.label}
							<div class="context-menu context-submenu" role="menu">
								{#each entry.commands as subcmd}
									{@const isCheckbox = subcmd.checkedState !== undefined}
									<button
										class="context-menu-item"
										class:context-menu-checkbox={isCheckbox}
										onclick={() => executeCommand(subcmd, isCheckbox)}
										role={isCheckbox ? 'menuitemcheckbox' : 'menuitem'}
										aria-checked={isCheckbox ? subcmd.checkedState === true : undefined}
									>
										{#if isCheckbox}
											<span class="context-menu-check">
												{#if subcmd.checkedState === true}&#10003;{:else if subcmd.checkedState === 'mixed'}&minus;{/if}
											</span>
										{:else if subcmd.icon && iconMap[subcmd.icon]}
											{@const IconComponent = iconMap[subcmd.icon]}
											<span class="context-menu-icon">
												<IconComponent size={16} />
											</span>
										{/if}
										<span class="context-menu-label"
											>{subcmd.displayLabel}</span
										>
										{#if getShortcutDisplay(subcmd.id)}
											<span class="context-menu-shortcut"
												>{getShortcutDisplay(subcmd.id)}</span
											>
										{/if}
									</button>
								{/each}
							</div>
						{/if}
					</div>
				{/if}
			{/each}
		{/each}
		{#if menuSections.size === 0}
			<div class="context-menu-empty">No commands available</div>
		{/if}
	</div>
{/if}

<style>
	.context-menu {
		background: var(--bg1, #ffffff);
		border: 1px solid var(--border, #e5e5e5);
		border-radius: 8px;
		box-shadow:
			0 10px 15px -3px rgba(0, 0, 0, 0.1),
			0 4px 6px -2px rgba(0, 0, 0, 0.05);
		min-width: 180px;
		max-width: 280px;
		padding: 4px;
		position: fixed;
		z-index: 10000;
	}

	.context-menu-section {
		color: var(--text-muted, #666666);
		font-size: 11px;
		font-weight: 600;
		padding: 6px 10px 4px;
		text-transform: uppercase;
		letter-spacing: 0.05em;
	}

	.context-menu-separator {
		background: var(--border, #e5e5e5);
		height: 1px;
		margin: 4px 0;
	}

	.context-menu-item {
		align-items: center;
		background: transparent;
		border: none;
		border-radius: 4px;
		color: var(--text, #1a1a1a);
		cursor: pointer;
		display: flex;
		font-size: 13px;
		gap: 8px;
		padding: 8px 10px;
		text-align: left;
		width: 100%;
		transition: background 0.1s ease;
	}

	.context-menu-item:hover,
	.context-menu-item.submenu-open {
		background: var(--bg2, #f5f5f5);
	}

	.context-menu-icon {
		display: flex;
		align-items: center;
		flex-shrink: 0;
		color: var(--text-muted, #666666);
	}

	.context-menu-label {
		flex: 1;
	}

	.context-menu-shortcut {
		color: var(--text-muted, #666666);
		font-family: ui-monospace, monospace;
		font-size: 11px;
		opacity: 0.7;
	}

	.context-menu-chevron {
		color: var(--text-muted, #666666);
		font-size: 10px;
	}

	.context-menu-check {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		width: 16px;
		font-size: 13px;
		color: var(--text, #1a1a1a);
	}

	.context-menu-submenu {
		position: relative;
	}

	.context-submenu {
		position: absolute;
		left: 100%;
		top: -4px;
		margin-left: 2px;
	}

	.context-menu-empty {
		color: var(--text-muted, #666666);
		font-size: 13px;
		padding: 12px 10px;
		text-align: center;
	}

	/* Dark theme */
	:global(.dark) .context-menu {
		background: var(--bg1, #262626);
		border-color: var(--border, #404040);
	}

	:global(.dark) .context-menu-item {
		color: var(--text, #ffffff);
	}

	:global(.dark) .context-menu-item:hover,
	:global(.dark) .context-menu-item.submenu-open {
		background: var(--bg2, #333333);
	}

	:global(.dark) .context-menu-separator {
		background: var(--border, #404040);
	}
</style>
